import { Currency, DirtLevelEnum, Incoterms, PaymentEventEnum, PaymentMethodEnum, TransportMethodEnum } from 'api';
import { MaterialComponentValueSchema } from 'features';
import { createIncotermDependentValidation, yup } from 'form';

import { UploadDocumentSchema } from '~/document';

import { variablePricingSchema } from './variablePricingSchema';

export const baseSalesOrderFormSchema = variablePricingSchema
  .shape({
    allowed_shipping_lines_ids: yup
      .array()
      .of(yup.number().required())
      .notRequired()
      .transform((value: { id: number; name: string }[]) => {
        return value.map((v) => v.id);
      }),
    basel_code_override: yup.string().optional(),
    buyer_agent_id: yup.number().notRequired(),
    buyer_currency: yup.string().oneOf(Object.values(Currency)).required(),
    buyer_material_component_values: yup
      .array()
      .of(MaterialComponentValueSchema)
      .min(1, 'At least one buyer material component is required.'),
    buyer_material_spec_id: yup.number().notRequired(),
    collection_site_id: createIncotermDependentValidation([Incoterms.EXW]),
    colour_id: yup.number().optional(),
    comments: yup.string().optional(),
    delivery_site_id: createIncotermDependentValidation([Incoterms.DAP, Incoterms.DDP]),
    deposit_percentage: yup.string().required(),
    dirt_level: yup
      .string()
      .oneOf(Object.values(DirtLevelEnum))
      .transform((value) => {
        return value === 'n/a' ? undefined : value;
      }),
    documents: yup.array().of(UploadDocumentSchema).optional(),
    due_date_direction: yup.number().required(),
    due_date_offset: yup.number().required(),
    end_processor_id: yup.number().required(),
    external_reference_number: yup.string().optional(),
    final_destination_site_id: yup.number().required(),
    form_id: yup.number().optional(),
    free_time_required: yup.number().min(0).integer().required(),
    grade_id: yup.number().required(),
    has_caps_and_labels: yup.boolean().optional(),
    icd_id: yup.number().optional(),
    incoterm: yup.string().oneOf(Object.values(Incoterms)).required(),
    internal_notes: yup.string().optional(),
    is_food_grade: yup.boolean().required(),
    is_trial_order: yup.boolean().required(),
    material_colour_id: yup.number().optional(),
    material_customs_name_override: yup.string().optional(),
    material_ewc_code_override: yup.string().optional(),
    material_id: yup.number().optional(),
    material_spec_id: yup.number().required(),
    max_number_of_shipments: yup.number().optional(),
    number_of_loads: yup.number().required(),
    origin_id: yup.number().optional(),
    payment_event: yup.string().oneOf(Object.values(PaymentEventEnum)).optional(),
    payment_method: yup.string().oneOf(Object.values(PaymentMethodEnum)).required(),
    payment_must_be_received_before_safi_releases_the_documents: yup.boolean().required(),
    port_of_discharge_id: createIncotermDependentValidation([
      Incoterms.CFR,
      Incoterms.CIF,
      Incoterms.CPT,
      Incoterms.CIP,
    ]),
    port_of_loading_id: createIncotermDependentValidation([Incoterms.FOB]),
    price_per_tonne: yup.string().required(),
    purchaser_id: yup.number().required(),
    seller_id: yup.number().optional(),
    seller_material_component_values: yup.array().of(MaterialComponentValueSchema).optional(),
    shipment_deadline_days: yup.number().optional(),
    site_material_id: yup.number().optional(),
    target_delivery_date: yup.string().required(),
    third_party_commission_currency: yup.string().oneOf(Object.values(Currency)).optional(),
    third_party_commission_per_tonne: yup.string().required(),
    tonnage_variance: yup.string().required(),
    tonnes: yup.string().required(),
    tonnes_per_load: yup.string().required(),
    transport_method: yup.string().oneOf(Object.values(TransportMethodEnum)).required(),
  })
  .required();
